import { inject } from '@angular/core';
import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { DialogService } from '@isaia/components/dialog';
import { catchError, throwError } from 'rxjs';
import { hasErrorPopup, HttpErrorPopup, HttpErrorPopupConfig } from './http-error.context';
import { HttpErrorException } from '@isaia/entity/http';
import { ERROR_MESSAGE } from '../error/error-code.formatter';
import { translate } from '@jsverse/transloco';
import { AuthService } from '../auth/auth.service';
import { hasAuthentication, shouldSkipAutoLogout } from '../auth/auth.context';

const HTTP_ERROR_POPUP_CLASS = 'isa-popup--http-error';

function getErrorException(e: HttpErrorResponse): Promise<HttpErrorException | null> {
  if (e?.error instanceof Blob) {
    try {
      return e.error.text().then((content) => {
        try {
          const errorObj = JSON.parse(content) as HttpErrorException;
          return errorObj || null;
        } catch {
          return null;
        }
      });
    } catch {
      return Promise.resolve(null);
    }
  }
  return Promise.resolve(e?.error as HttpErrorException);
}

async function getErrorMessage(e: HttpErrorResponse, config: HttpErrorPopupConfig): Promise<string> {
  const showDebugUrl = config.debug !== false;
  const exception = await getErrorException(e);
  const code = exception?.error?.code;
  const messageFromErrorPopup = typeof config.message === 'function' ? config.message(e) : config.message;
  const messageFormatted = messageFromErrorPopup || ERROR_MESSAGE[code!]?.(translate) || '';
  const messageDetail = (exception?.error?.detail || '').trim();
  const message = messageFormatted || messageDetail;
  const debug = message ? '' : showDebugUrl ? e.message || 'Unknown error' : '';
  return [message, debug].filter((v) => !!v).join('\n\n');
}

const isUnauthorized = (e: HttpErrorResponse) => e.status === HttpStatusCode.Unauthorized;

export const httpErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  // A 401 means "session expired" only on authenticated requests; on unauthenticated
  // ones (e.g. the dev-login POST) it means the credentials were rejected.
  const isSessionExpired = (e: HttpErrorResponse) => isUnauthorized(e) && hasAuthentication(req);
  // Requests marked withoutAutoLogout handle their own 401 (e.g. the init-time
  // profile load), so the global logout redirect must not fire for them.
  const autoLogout = !shouldSkipAutoLogout(req);
  const errorPopup = hasErrorPopup(req);
  if (typeof errorPopup === 'boolean' && !errorPopup) {
    return next(req).pipe(
      catchError((e: HttpErrorResponse) => {
        if (isSessionExpired(e) && autoLogout) {
          authService.logout();
        }
        return throwError(() => e);
      }),
    );
  }

  const dialogService = inject(DialogService);
  return next(req).pipe(
    catchError((e: HttpErrorResponse) => {
      if (isSessionExpired(e)) {
        if (autoLogout) {
          authService.logout();
        }
        // we don't want to show any error message so we throwError here
        return throwError(() => e);
      }

      const exception = e.error as HttpErrorException;
      const config = getErrorPopupConfig(errorPopup, e);
      if (config) {
        const title = config.title || exception?.error?.title || 'Http Error';
        getErrorMessage(e, config).then((message) => {
          dialogService.openPopup({ title, message }, { panelClass: HTTP_ERROR_POPUP_CLASS, disableClose: config.disableClose });
        });
      }
      return throwError(() => e);
    }),
  );
};

function getErrorPopupConfig(errorPopup: HttpErrorPopup, e: HttpErrorResponse) {
  const shouldOpenPopup = typeof errorPopup === 'function' ? errorPopup(e) : errorPopup;

  if (typeof shouldOpenPopup === 'object') {
    return shouldOpenPopup;
  }

  if (shouldOpenPopup) {
    // we return an empty config, so we can show the popup with the all fallback values
    return {} as HttpErrorPopupConfig;
  }

  return undefined;
}
